import { useLayoutEffect, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import PatientListTableSkeleton from "./PatientListTableSkeleton";
import { CELL_PADDING, headerAlignClass, cellAlignClass } from "./helpers";
import TriangleIcon from "../../ui/icons/TriangleIcon";

const SKELETON_ROW_HEIGHT_PX = 64;
const FALLBACK_SKELETON_ROW_COUNT = 8;

// `sortDirection`: false | "asc" | "desc"
function SortIndicator({ sortDirection }) {
    return (
        <span className="flex flex-col items-center -space-y-1.5">
            <TriangleIcon
                className={sortDirection === "asc" ? "" : "opacity-30"}
                size={14}
            />
            <TriangleIcon
                className={sortDirection === "desc" ? "" : "opacity-30"}
                size={14}
                direction="down"
            />
        </span>
    );
}

function PatientListTable({ table, columns, loadComplete, scrollRef }) {
    const rows = table.getRowModel().rows;
    const leafColumns = table.getVisibleLeafColumns();

    const [skeletonRowCount, setSkeletonRowCount] = useState(
        FALLBACK_SKELETON_ROW_COUNT
    );

    useLayoutEffect(() => {
        const measure = () => {
            const height = scrollRef.current?.clientHeight;
            if (height) {
                setSkeletonRowCount(
                    Math.max(1, Math.ceil(height / SKELETON_ROW_HEIGHT_PX))
                );
            }
        };
        measure();
        window.addEventListener("resize", measure);

        return () => window.removeEventListener("resize", measure);
    }, [scrollRef]);

    const totalColumnWidth = leafColumns.reduce(
        (total, column) => total + column.getSize(),
        0
    );

    const colGroup = (
        <colgroup>
            {leafColumns.map((column) => (
                <col
                    key={column.id}
                    style={{
                        width: `${(column.getSize() / totalColumnWidth) * 100}%`,
                    }}
                />
            ))}
        </colgroup>
    );

    return (
        <div className="tw-border flex min-h-0 flex-1 flex-col overflow-hidden rounded border-neutral-300 bg-white">
            <div className="thin-scrollbar flex min-h-0 flex-1 flex-col overflow-x-auto overflow-y-hidden">
                <table
                    style={{ minWidth: totalColumnWidth }}
                    className="w-full table-fixed whitespace-nowrap"
                >
                    {colGroup}
                    <thead className="h-8 bg-[#f8f8f8]">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        id={`patient-list-heading-${header.id}`}
                                        className={`${CELL_PADDING} shadow-[inset_0_-1px_0_var(--color-neutral-300)] ${
                                            header.column.getCanSort()
                                                ? "cursor-pointer"
                                                : ""
                                        }`}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <div
                                            className={`flex items-center gap-x-2 text-neutral-600 ${headerAlignClass(header.column.columnDef)}`}
                                        >
                                            <span
                                                id={`patient-list-heading-txt-${header.id}`}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext()
                                                )}
                                            </span>
                                            {header.column.getCanSort() && (
                                                <SortIndicator
                                                    sortDirection={header.column.getIsSorted()}
                                                />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                </table>
                <div
                    ref={scrollRef}
                    style={{ minWidth: totalColumnWidth }}
                    className="thin-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto"
                >
                    <table className="w-full table-fixed whitespace-nowrap">
                        {colGroup}
                        <tbody>
                            {loadComplete ? (
                                rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="h-16 border-b border-neutral-200 last:border-b-0 hover:bg-gray-100"
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className={`${CELL_PADDING} ${cellAlignClass(cell.column.columnDef)}`}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <PatientListTableSkeleton
                                    columns={columns}
                                    rowCount={skeletonRowCount}
                                />
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default PatientListTable;
